import { v4 as uuidv4 } from 'uuid'
import { NotFound } from './errors'
import instanceQueryService from './instanceQueryService'
import query from './sql'
import * as functions from '@/util/functions'
import { parseFilter } from '@/services/expressions'
import Cluster from '@/models/Cluster'
import Database from '@/models/Database'
import Format from '@/models/Format'
import Function from '@/models/Function'
import Instance from '@/models/Instance'
import Location from '@/models/Location'
import Procedure from '@/models/Procedure.js'
import Role from '@/models/Role'
import Schema from '@/models/Schema'
import Sequence from '@/models/Sequence'
import Storage from '@/models/Storage'
import Table from '@/models/Table'
import View from '@/models/View'
import WLMProfile from '@/models/WLMProfile'
import WLMResourcePool from '@/models/WLMResourcePool'
import WLMRule from '@/models/WLMRule'

const CACHE_EXPIRATION = (15 * 60 * 1000) // 15min cache expiration

const OBJECT_IDENTIFIER_RE = /\{\{id\}\}/g // For replacing {{id}} in queries below
const OBJECT_TYPES = {
  system: {
    storedPrivilegesQuery: query.privilegesSystem,
    privileges: [
      'ALTER ANY DATABASE',
      'ALTER ANY ROLE',
      'ALTER ANY CLUSTER',
      'BACKUP ANY DATABASE',
      'BULK LOAD',
      'CONTROL ANY SESSION',
      'CONTROL CACHE',
      'CONTROL EXTERNAL AUTHENTICATION',
      'CONTROL LDAP',
      'CONTROL WLM',
      'CREATE CLUSTER',
      'CREATE DATABASE',
      'CREATE EXTERNAL FORMAT',
      'CREATE EXTERNAL LOCATION',
      'CREATE EXTERNAL STORAGE',
      'CREATE ROLE',
      'DROP ANY DATABASE',
      'DROP ANY EXTERNAL FORMAT',
      'DROP ANY EXTERNAL LOCATION',
      'DROP ANY EXTERNAL STORAGE',
      'DROP ANY ROLE',
      'DROP ANY CLUSTER',
      'EXPLAIN QUERY',
      'RESTORE ANY DATABASE',
      'TRACE QUERY',
      'USAGE ANY CLUSTER',
      'USAGE ANY EXTERNAL FORMAT',
      'USAGE ANY EXTERNAL LOCATION',
      'USAGE ANY EXTERNAL STORAGE',
      'VIEW QUERY TEXT',
      'VIEW ROLE'
    ]
  },
  database: {
    storedPrivilegesQuery: query.privilegesDatabase,
    privileges: [
      'ALTER ANY SCHEMA',
      'BACKUP',
      'BULK LOAD',
      'CONNECT',
      'CREATE',
      'DROP ANY SCHEMA',
      'EXPLAIN QUERY',
      'RESTORE',
      'TEMPORARY',
      'TRACE QUERY',
      'VIEW QUERY TEXT'
    ]
  },
  cluster: {
    storedPrivilegesQuery: query.privilegesCluster,
    privileges: [
      'ALTER CLUSTER',
      'DROP CLUSTER',
      'USAGE'
    ]
  },
  schema: {
    storedPrivilegesQuery: query.privilegesSchema,
    privileges: [
      'CREATE',
      'USAGE'
    ]
  },
  table: {
    storedPrivilegesQuery: query.privilegesTable,
    privileges: [
      'DELETE',
      'INSERT',
      'REFERENCES',
      'SELECT',
      'TRUNCATE',
      'UPDATE'
    ]
  },
  view: {
    storedPrivilegesQuery: query.privilegesView,
    privileges: [
      'SELECT'
    ],
    typeOverride: 'table'
  },
  procedure: {
    storedPrivilegesQuery: query.privilegesProcedure,
    privileges: [
      'EXECUTE'
    ],
    typeOverride: 'function'
  },
  sequence: {
    storedPrivilegesQuery: query.privilegesSequence,
    privileges: [
      'USAGE',
      'SELECT',
      'UPDATE'
    ]
  },
  role: {
    storedPrivilegesQuery: query.privilegesRole,
    privileges: [
      'USAGE',
      'MEMBER'
    ]
  },
  member: {
    storedPrivilegesQuery: query.privilegesMember,
    privileges: [
      'USAGE',
      'MEMBER'
    ]
  },
  external_format: {
    storedPrivilegesQuery: query.privilegesExternalFormat,
    privileges: [
      'ALTER EXTERNAL FORMAT',
      'DROP EXTERNAL FORMAT',
      'USAGE EXTERNAL FORMAT'
    ]
  },
  external_storage: {
    storedPrivilegesQuery: query.privilegesExternalStorage,
    privileges: [
      'ALTER EXTERNAL STORAGE',
      'DROP EXTERNAL STORAGE',
      'USAGE EXTERNAL STORAGE'
    ]
  },
  external_location: {
    storedPrivilegesQuery: query.privilegesExternalLocation,
    privileges: [
      'ALTER EXTERNAL LOCATION',
      'DROP EXTERNAL LOCATION',
      'USAGE EXTERNAL LOCATION'
    ]
  }
}

class DatabaseObjectService {

  async populate(instanceId, database, options = { full: true }) {
    const { full, skiptables } = options
    if (!instanceId) {
      throw new Error(`Parameter 'instanceId' is required for populate`)
    }
    if (typeof instanceId !== 'string') {
      throw new TypeError(`Parameter 'instanceId' is not valid`)
    }
    const instance = Instance.query().whereId(instanceId).first()
    if (!instance) {
      throw new NotFound(`Could not locate instance: ${instanceId}`)
    }
    if (!instance.online) {
      return true // We can't populate a suspended instance.
    }
    // Populate the databases.
    let databases = Database.query().where('instance_id', instance.id).get()
    if (!databases.length) {
      databases = (await this.populateDatabases(instance.id))
      Database.insertWithInstance(instance.id, { data: databases })
      databases = Database.query().where('instance_id', instance.id).get()
    }
    if (!database) {
      return true
    }

    // Examine database object; is it populating, or is it fresh enough (cached)?
    const databaseObject = databases.find(d => d.name === database)
    if (!databaseObject) {
      throw new NotFound('Could not locate database: ' + database)
    }
    const { database_id } = databaseObject
    if (databaseObject.populating) {
      false && console.log('NOT populating already populating', database)
      return true // we are populating already.
    }
    if (!!databaseObject.populated && +new Date() < databaseObject.populated + CACHE_EXPIRATION) {
      false && console.log('NOT populating already populated', database, 'cache expires', new Date(databaseObject.populated + CACHE_EXPIRATION))
      return true // we haven't expired our cache.
    }
    await Database.update({
      where: [instance.id, database_id],
      data: {
        populating: true
      }
    })
    try {
      false && console.log('populating', database, database_id)

      // Populate the objects.
      const [
        roles,
        schemas,
        tables,
        views,
        sequences,
        funcs,
        procedures,
        storages,
        locations,
        formats,
        clusters
      ] = await Promise.all([
        this.populateRoles(instance.id, database),
        this.populateSchemas(instance.id, database, database_id),
        !skiptables ? this.populateTables(instance.id, database, database_id, 0, 0) : Promise.resolve([]),
        this.populateViews(instance.id, database, database_id),
        this.populateSequences(instance.id, database, database_id),
        full ? this.populateFunctions(instance.id, database, database_id) : Promise.resolve([]),
        this.populateProcedures(instance.id, database, database_id),
        full ? this.populateStorages(instance.id, database, database_id) : Promise.resolve([]),
        full ? this.populateLocations(instance.id, database, database_id) : Promise.resolve([]),
        full ? this.populateFormats(instance.id, database, database_id) : Promise.resolve([]),
        this.populateClusters(instance.id, database)
      ])

      // Populate the store.
      await Promise.all([
        !roles ? Promise.resolve([]) : Role.insertWithInstance(instance.id, { data: roles }, null, true),
        Schema.insertWithInstance(instance.id, { data: schemas }, database_id, true),
        Table.insertWithInstance(instance.id, { data: tables }, database_id, true),
        View.insertWithInstance(instance.id, { data: views }, database_id, true),
        Sequence.insertWithInstance(instance.id, { data: sequences }, database_id, true),
        Function.insertWithInstance(instance.id, { data: funcs }, database_id, true),
        Procedure.insertWithInstance(instance.id, { data: procedures }, database_id, true),
        Storage.insertWithInstance(instance.id, { data: storages }),
        Location.insertWithInstance(instance.id, { data: locations }),
        Format.insertWithInstance(instance.id, { data: formats }),
        Cluster.insertWithInstance(instance.id, { data: clusters })
      ])

      // We are now populated.  Update populated state.
      await Database.update({
        where: [instance.id, database_id],
        data: {
          populated: full ? +new Date() : 0
        }
      })
    } finally {
      // Done populating.
      await Database.update({
        where: [instance.id, database_id],
        data: {
          populating: false
        }
      })
    }

    return true
  }

  async repopulate(instanceId, database) {
    // Clean out the data model for this database.
    const databaseObject = Database.query().where('instance_id', instanceId).where('name', database).first()
    if (!!databaseObject && !!databaseObject.populated) {
      Database.update({
        where: [instanceId, databaseObject.database_id],
        data: {
          populated: 0
        }
      })

      // Remove references to this database.
      if (false) { // Database objects
        Schema.delete(item => item.instance_id === instanceId && item.database_id === databaseObject.database_id)
        Table.delete(item => item.instance_id === instanceId && item.database_id === databaseObject.database_id)
        View.delete(item => item.instance_id === instanceId && item.database_id === databaseObject.database_id)
        Sequence.delete(item => item.instance_id === instanceId && item.database_id === databaseObject.database_id)
        Function.delete(item => item.instance_id === instanceId && item.database_id === databaseObject.database_id)
        Procedure.delete(item => item.instance_id === instanceId && item.database_id === databaseObject.database_id)
      }
      if (false) { // Global objects
        Role.delete(item => item.instance_id === instanceId)
        Storage.delete(item => item.instance_id === instanceId)
        Location.delete(item => item.instance_id === instanceId)
        Format.delete(item => item.instance_id === instanceId)
        Cluster.delete(item => item.instance_id === instanceId)
      }
    }

    // Issue populate.
    try {
      return await this.populate(instanceId, database)
    } catch (e) {
      if (!(e instanceof NotFound)) {
        throw e
      }
    }
  }

  async evict(instanceId) {
    const databaseObjects = Database.query().where('instance_id', instanceId).get()
    await Promise.all([
      Role.delete(item => item.instance_id === instanceId),
      Schema.delete(item => item.instance_id === instanceId),
      Table.delete(item => item.instance_id === instanceId),
      View.delete(item => item.instance_id === instanceId),
      Sequence.delete(item => item.instance_id === instanceId),
      Function.delete(item => item.instance_id === instanceId),
      Procedure.delete(item => item.instance_id === instanceId),
      Storage.delete(item => item.instance_id === instanceId),
      Location.delete(item => item.instance_id === instanceId),
      Format.delete(item => item.instance_id === instanceId),
      Cluster.delete(item => item.instance_id === instanceId),
      ...databaseObjects.map(databaseObject => Database.update({
        where: [instanceId, databaseObject.database_id],
        data: {
          populated: 0
        }
      })
      )
    ])
  }

  async populateTableSummary(instance_id, database, schema_id, limit = undefined, offset = undefined, sort = undefined, filter = undefined, count = false) {
    const parameters = { columns: count ? 'count(*)' : '*', schema_id }

    // Load and format initial sql with inner table.
    let sql = query.populateTableSummary

    // Add filter.
    if (filter) {
      const and = []
      parseFilter(and, filter, ['name', 'owner_name'])
      sql += and.join('\n')
    }

    // Add sort.
    const orderBy = []
    if (!count && !!sort) {
      let nextParam = 0
      sort.forEach(({ colId, sort }) => {
        if (!!sort && !sort.match(/asc|desc/i)) {
          throw new Error(`Invalid sort column: ${colId} ${sort}`)
        }
        const column = `col${nextParam++}`
        parameters[column] = colId
        orderBy.push(`$(${column}:name) ${sort} nulls last`)
      })
    }
    if (orderBy.length > 0) {
      sql += `order by ${orderBy.join(', ')}\n`
    }

    // Add offset/limit.
    if (typeof limit !== 'undefined') {
      sql += `limit $(limit)\n`
      parameters.limit = limit
    }
    if (typeof offset !== 'undefined') {
      sql += `offset $(offset)\n`
      parameters.offset = offset
    }

    const response = await instanceQueryService.sql(instance_id, database, sql, { parameters })
    return count
      ? response.rows[0].count
      : Object.freeze((response.rows || []).map((table) => {
        table.table_id = table.id
        table.__type = 'table'
        table._owner_name = table.owner_name
        return table
      }))
  }

  async populateSchemaSummary(instance_id, database, database_id) {
    const response = await instanceQueryService.sql(instance_id, database, query.populateSchemaSummary)
    return response.rows.map((row) => {
      row.database_id = database_id
      return row
    })
      .filter(Schema.filterCommon)
  }

  async populateTablesRowstoreData(instanceId, database) {
    const response = await instanceQueryService.sql(instanceId, database, query.populateTablesRowstoreData, { parameters: { database } })
    return Object.freeze(functions.indexBy(response.rows || [], 'table_id'))
  }

  async populateDatabases(instanceId) {
    const response = await instanceQueryService.sql(instanceId, 'yellowbrick', query.populateDatabases)
    return (response.rows || [])
  }

  async populateSchemas(instanceId, database, database_id) {
    if (!database) {
      return []
    }

    const response = await instanceQueryService.sql(instanceId, database, query.populateSchemas, { parameters: { database_id } })
    return (response.rows || [])
      .map((schema) => {
        schema.populated = true
        return schema
      })
  }

  async populateRoles(instanceId, database, allowSystemRoles) {
    if (!database) {
      return []
    }
    const usersResponse = instanceQueryService.sql(instanceId, database, query.populateUsers, { parameters: { base_id: allowSystemRoles ? 0 : 16384 } })
    const rolesResponse = instanceQueryService.sql(instanceId, database, query.populateRoles, { parameters: { base_id: allowSystemRoles ? 0 : 16384 } })
    const results = await Promise.all([usersResponse, rolesResponse])
    const users = (results[0].rows || []).map((r) => {
      return {
        ...r,
        id: r.user_id,
        __type: 'user'
      }
    }).filter(r => !r.name !== 'cdwmdiscsrva')
    const roles = (results[1].rows || []).map((r) => {
      return {
        ...r,
        id: r.role_id,
        __type: 'role'
      }
    }).filter(r => !r.name.match(/^sys_ybd_smc/))
    return [...users, ...roles]
  }

  async populateTables(instanceId, database, database_id, limit = 0, offset = 0) {
    if (!database) {
      return []
    }
    const parameters = { database_id }
    let sql = query.populateTables
    if (limit) {
      sql += `\nLIMIT $(limit)`
      parameters.limit = limit
    }
    if (offset) {
      sql += `\nOFFSET $(offset)`
      parameters.offset = offset
    }
    const response = await instanceQueryService.sql(instanceId, database, sql, { parameters })
    return (response.rows || [])
  }

  async populateViews(instanceId, database, database_id) {
    if (!database) {
      return []
    }
    const response = await instanceQueryService.sql(instanceId, database, query.populateViews, { parameters: { database_id } })
    return (response.rows || [])
  }

  async populateSequences(instanceId, database, database_id) {
    if (!database) {
      return []
    }
    const response = await instanceQueryService.sql(instanceId, database, query.populateSequences, { parameters: { database_id } })
    return (response.rows || [])
      .map((sequence) => {
        const parts = sequence.fullname?.split(/\./g)
        if (parts?.length === 2) {
          [sequence.schema, sequence.name] = parts
        } else {
          sequence.schema = 'public'
          sequence.name = sequence.fullname
        }
        return sequence
      })
  }

  async populateFunctions(instanceId, database, database_id) {
    if (!database) {
      return []
    }
    const response = await instanceQueryService.sql(instanceId, database, query.populateFunctions, { parameters: { database_id } })
    return (response.rows || []).map((_) => {
      _.function_id = _.oid
      _.owner_id = _.proowner
      _.schema = _.nspname
      _.name = _.proname
      return _
    })
  }

  async populateProcedures(instanceId, database, database_id) {
    if (!database) {
      return []
    }
    const response = await instanceQueryService.sql(instanceId, database, query.populateProcedures)
    return (response.rows || [])
  }

  async populateStorages(instanceId, database, database_id) {
    if (!database) {
      return []
    }
    const response = await instanceQueryService.sql(instanceId, database, query.populateExternalStorages)
    return (response.rows || [])
  }

  async populateLocations(instanceId, database, database_id) {
    if (!database) {
      return []
    }
    const response = await instanceQueryService.sql(instanceId, database, query.populateExternalLocations)
    return (response.rows || [])
  }

  async populateFormats(instanceId, database, database_id) {
    if (!database) {
      return []
    }
    const response = await instanceQueryService.sql(instanceId, database, query.populateExternalFormats)
    return (response.rows || [])
  }

  async populateClusters(instanceId, database) {
    const instance = Instance.query().whereId(instanceId).first()
    if (!instance) {
      throw new NotFound(`Could not locate instance: ${instanceId}`)
    }
    if (!database) {
      return []
    }
    const versionPrior62 = parseFloat(instance?.softwareVersion || '6.1') < 6.2
    const error_message = versionPrior62 ? 'null' : 'error_message'
    const result = await instanceQueryService.sql(instance?.id, 'yellowbrick', query.populateClusters, { parameters: { error_message } })
    return (!!result && !!result.rows && !!result.rows.length && result.rows) || []
  }

  async populateHardwareInstanceTypes(instanceId, database) {
    if (!database) {
      return []
    }
    // NB: the sys.hardware_instance_type view actually talks to K8S CRD, so if its slow we want a request all by itself,
    // hence the batchKey here.
    const hwTypesQuery = instanceQueryService.sql(instanceId, 'yellowbrick', query.populateHardwareInstanceTypes, 0, { batchKey: uuidv4() })
    let result = null
    try {
      result = await hwTypesQuery
    } catch (error) {

    }
    return (!!result && !!result.rows && !!result.rows.length && result.rows) || []
  }

  async populateWlm(instanceId) {
    const instance = Instance.query().whereId(instanceId).first()
    if (!instance) {
      throw new NotFound(`Could not locate instance: ${instanceId}`)
    }
    if (instance.isInstancePopulatingWLM) {
      return
    }
    if (!instance.online) {
      return
    }
    try {
      // Set populating flag.
      await Instance.update({
        where: instanceId,
        data: {
          isInstancePopulatingWLM: true
        }
      })

      // Retrieve WLM config.
      const result = await instanceQueryService.sql(instance.id, 'yellowbrick', query.populateWlm)
      if (result && !result.error && result.rowSets[0]?.rows && result.rowSets[2]?.rows) {
        const wlmUsageMap = functions.indexBy(result.rowSets[2].rows, 'active_wlm_profile_name')
        WLMProfile.insertWithInstance(instanceId, {
          data: result.rows.map(row => ({
            ...row,
            ...{
              clusters: (wlmUsageMap[row.name]?.clusters || []).join(', ')
            }
          }))
        })
        WLMResourcePool.insertWithInstance(instanceId, {
          data: result.rowSets[0].rows.map(row => ({
            ...row
          }))
            .filter(p => p.name !== 'system') // Oh god no! hack!
        })
        WLMRule.insertWithInstance(instanceId, { data: result.rowSets[1].rows })
      }
    } finally {
      // Set populating flag.
      await Instance.update({
        where: instanceId,
        data: {
          isInstancePopulatingWLM: false
        }
      })
    }
  }

  async getViewDefinition(instanceId, database, database_id, view_id) {
    const response = await instanceQueryService.sql(instanceId, database, query.getViewDefinition, { parameters: { database_id, view_id } })
    return (response.rows || [])[0].definition
  }

  async getComment(instanceId, database, id) {
    if (!database) {
      return ''
    }
    const response = await instanceQueryService.sql(instanceId, database, query.getComment, { parameters: { id } })
    return response.rows[0]?.description
  }

  async setComment(instanceId, database, type, thing, comment) {
    await instanceQueryService.sql(instanceId, database, query.commentOn, { parameters: { type, thing, comment } })
  }

  async getRelationships(instanceId, database, schema_id) {
    const remote = [
      instanceQueryService.sql(instanceId, database, query.getRelationshipsTables, { parameters: { schema_id } }),
      instanceQueryService.sql(instanceId, database, query.getRelationships, { parameters: { schema_id } }),
      instanceQueryService.sql(instanceId, database, query.getRelationshipsColumns, { parameters: { schema_id } })
    ]
    const results = await Promise.all(remote)
    const { rows: tables } = results[0]
    const { rows: relationships } = results[1]
    const { rows: columns } = results[2]
    return { tables, relationships, columns }
  }

  async getSchemaDetails(instanceId, database, schema, maxMCV) {
    const response = await instanceQueryService.sql(instanceId, database, query.getSchemaDetails, { parameters: { database, schema, maxMCV } })
    if (!response || !response.rows || !Array.isArray(response.rows)) {
      throw new Error("Invalid response or no rows found.")
    }

    const tableNames = [...new Set(response.rows.map(row => row.table_name))]
    const tableDefinitions = await Promise.all(
      tableNames.map(async (tableName) => {
        try {
          const definition = await this.getTableDefinition(instanceId, database, schema, tableName)
          return { tableName, definition }
        } catch (err) {
          return { tableName, definition: null }
        }
      })
    )

    const definitionMap = new Map(
      tableDefinitions.map(({ tableName, definition }) => [tableName, definition])
    )
    return response.rows.map(row => ({
      schemaName: schema,
      tableName: row.table_name,
      tableDefinition: definitionMap.get(row.table_name),
      column: row.column_name,
      mostCommonValues: row.most_common_vals || []
    }))
  }

  async getTableDefinition(instanceId, database, schemaName, tableName) {
    let target = tableName
    if (schemaName) {
      target = schemaName + '.' + tableName
    }
    const response = await instanceQueryService.sql(instanceId, database, query.getTableDefinition, { parameters: { target } })
    if (!!response && !!response.rows && Array.isArray(response.rows) && response.rows.length > 0) {
      let definition = response.rows[0].yb_describe
      if (!!definition) {
        definition = '\n' + definition.replace(/^--.*\n?/gm, '')
      }
      return definition
    } else {
      return null
    }
  }

  async getClusterUsage(instanceId, database, interval) {
    return await instanceQueryService.sql(instanceId, database, query.clusterUsage, { parameters: { interval } })
  }

  /**
   * Given an object type and id, return the privileges a given user has capability for,
   * whether explicitly defined or defined by role inheritance.  This returns the
   * "effective privileges", including indicator of which privileges can be granted to others.
   *
   * The shape of returned tuples is [{privilege_type, granted, is_grantable}, ...]
   * @param {Instance} instance - the instance
   * @param {string} database - the database to issue the query within
   * @param {string} type - the type of object (eg. system, database, table, etc.)
   * @param {number|array} idOrIds - the identifier(s) of the object (can be nullish/missing for 'system')
   * @param {number} roleOrUserId - the identifier of the user or role being queries or nullish/missing for current logged in user
   * @returns grants for given object or empty array
   */
  async getRoleObjectPrivileges(instanceId, database, type = 'system', idOrIds = 0, roleOrUserId = 0) {
    // Get the privileges asked for.
    const objectType = OBJECT_TYPES[type]
    if (!objectType) {
      throw new Error(`Invalid object type: ${type}`)
    }
    const { privileges } = objectType
    type = objectType.typeOverride || type

    // Construct SQL.
    const grantableName = privilege =>
      `${privilege} WITH ${type === 'role' ? 'ADMIN' : 'GRANT'} OPTION`
    const systemClusterPrivilege = (privilege) => {
      switch (privilege) {
        case 'ALTER CLUSTER': return 'ALTER ANY CLUSTER'
        case 'DROP CLUSTER': return 'DROP ANY CLUSTER'
        case 'USAGE': return 'USAGE ANY CLUSTER'
        case 'ALTER CLUSTER WITH GRANT OPTION': return 'ALTER ANY CLUSTER WITH GRANT OPTION'
        case 'DROP CLUSTER WITH GRANT OPTION': return 'DROP ANY CLUSTER WITH GRANT OPTION'
        case 'USAGE WITH GRANT OPTION': return 'USAGE ANY CLUSTER WITH GRANT OPTION'
      }
      throw new Error('Unmapped cluster system privilege: ' + privilege)
    }
    const parameters = { roleOrUserId }
    const hasPrivilege = (privilege, idOrIds) => { // NB: privilige is a non-user-input string; no need to parametrize/format
      let result = ' '

      result += (Array.isArray(idOrIds) ? idOrIds : [idOrIds]).map((id, index) => {
        let privilegeSql = ''

        if (type === 'role') {
          privilegeSql += 'pg_has_role('
          if (roleOrUserId) {
            privilegeSql += '$(roleOrUserId), '
          }
          if (id) {
            const idKey = `id${index}`
            parameters[idKey] = id
            privilegeSql += `$(${idKey}), `
          }
          privilegeSql += `'${privilege}') as "${privilege}"`
        } else {
          privilegeSql += `has_${type}_privilege(`
          if (roleOrUserId) {
            privilegeSql += '$(roleOrUserId), '
          }
          if (id) {
            const idKey = `id${index}`
            parameters[idKey] = id
            privilegeSql += `$(${idKey}), `
          }
          privilegeSql += `'${privilege}')`
          if (type === 'cluster') {
            privilegeSql = `(${privilegeSql} or has_system_privilege('${systemClusterPrivilege(privilege)}'))`
          }
        }
        return privilegeSql
      }).join(' and ')
      result += ` as "${privilege}"`

      return result
    }
    const privilegesSql = `SELECT\n` +
      privileges.map(privilege => hasPrivilege(privilege, idOrIds)).join(',\n') + ',\n' +
      privileges.map(privilege => hasPrivilege(grantableName(privilege), idOrIds)).join(',\n')

    // Make query and return tuples: { privilege_type, granted, is_grantable }
    const {
      rows: [privilegesResult]
    } = await instanceQueryService.sql(instanceId, database, privilegesSql, { parameters })
    return privileges.map((privilege_type) => {
      return { privilege_type, granted: !!privilegesResult[privilege_type], is_grantable: !!privilegesResult[grantableName(privilege_type)] }
    })
  }

  /**
   * Given an object type and id, return the grants stored.
   *
   * The shape of returned tuples is [{grantee, privilege_type, is_grantable}, ...]
   * @param {Instance} instance - the instance address
   * @param {string} database - the database to issue the query within
   * @param {string} type - the type of object (eg. system, database, table, etc.)
   * @param {number|array} idOrIds - the identifier(s) of the object (can be nullish/missing for 'system')
   * @returns grants for given object(s) or empty array
   */
  async getStoredObjectPrivileges(instanceId, database, type, idOrIds) {
    // Get the object type asked for.
    const objectType = OBJECT_TYPES[type]
    if (!objectType) {
      throw new Error(`Invalid object type: ${type}`)
    }
    const parameters = {}
    const privilegesSql = (Array.isArray(idOrIds) ? idOrIds : [idOrIds]).map((id, index) => {
      const idKey = `id${index}`
      const idParameter = `$(${idKey})`
      parameters[idKey] = id
      return objectType.storedPrivilegesQuery.replace(OBJECT_IDENTIFIER_RE, idParameter)
    }).join('\nINTERSECT\n')

    const result = await instanceQueryService.sql(instanceId, database, privilegesSql, { parameters })
    return result.rows
  }

  getObjectPrivilegeNames(type) {
    const objectType = OBJECT_TYPES[type]
    if (!objectType) {
      throw new Error(`Invalid object type: ${type}`)
    }
    return objectType.privileges
  }

  async concurrentQueries(instanceId, startTime, endTime, clusterName) {
    const response = await instanceQueryService.sql(instanceId, 'yellowbrick', query.concurrentQueries, {
      parameters: {
        start: startTime.toISOString(),
        end: endTime.toISOString(),
        cluster_name: clusterName
      }
    })
    return response.rows
  }

  async poolsAtTime(instanceId, queryId) {
    const response = await instanceQueryService.sql(instanceId, 'yellowbrick', query.poolsAtTime, { parameters: { queryId } })
    return response.rows
  }
}

const databaseObjectService = new DatabaseObjectService()
export default databaseObjectService
